import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ShipAssureLogo from '../common/ShipAssureLogo';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiShield, FiVideo, FiShare2, FiCloud, FiZap, FiSmartphone,
  FiCheckCircle, FiTrendingUp, FiAlertOctagon, FiUsers,
  FiPackage, FiArrowRight, FiStar, FiMenu, FiX, FiPlay,
  FiMail, FiDownload, FiClock, FiMonitor, FiCamera, FiWeight,
  FiXCircle, FiGift, FiCreditCard
} = FiIcons;

// ── Fade-up animation wrapper ──────────────────────────────────────────────
const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ── Navbar ─────────────────────────────────────────────────────────────────
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <ShipAssureLogo size={36} />
          <div className="flex flex-col leading-none">
            <span className="font-extrabold text-xl tracking-tight">
              <span className="text-gray-900">Ship</span>
              <span className="text-blue-600">Assure</span>
            </span>
            <span className="text-[10px] text-gray-400 font-medium hidden sm:block">Turn Every Shipment into Verified Proof</span>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-gray-600">
          {[['features', 'Features'], ['how-it-works', 'How It Works'], ['testimonials', 'Testimonials']].map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} className="hover:text-blue-600 transition-colors">{label}</button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={() => navigate('/login')} className="text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors px-3 py-1.5">Login</button>
          <button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-5 py-2 rounded-full shadow-sm transition-all hover:shadow-blue-200 hover:shadow-md">
            Get Started Free
          </button>
        </div>

        <button className="md:hidden p-2 text-gray-600" onClick={() => setMobileOpen(o => !o)}>
          <SafeIcon icon={mobileOpen ? FiX : FiMenu} className="text-xl" />
        </button>
      </div>

      {mobileOpen && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {[['features', 'Features'], ['how-it-works', 'How It Works'], ['testimonials', 'Testimonials']].map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} className="text-left text-sm font-medium text-gray-700 hover:text-blue-600">{label}</button>
          ))}
          <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
            <button onClick={() => navigate('/login')} className="text-sm font-semibold text-gray-700 text-left">Login</button>
            <button onClick={() => navigate('/signup')} className="bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-full">Get Started Free</button>
          </div>
        </motion.div>
      )}
    </header>
  );
};

// ── Hero ───────────────────────────────────────────────────────────────────
const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-white pt-16">
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl -z-0" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-100/40 rounded-full blur-3xl -z-0" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            Trusted by 1000+ D2C Brands
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.1] tracking-tight mb-6">
            Turn Every Shipment into{' '}
            <span className="text-blue-600 relative">
              Verified Proof
              <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 300 8" fill="none">
                <path d="M0 6 Q75 0 150 5 Q225 10 300 4" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.4" />
              </svg>
            </span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-500 leading-relaxed mb-6 max-w-lg">
            Record real packaging videos using your <strong className="text-gray-700">webcam & PC</strong> and share them with customers via email to build trust and eliminate disputes.
          </motion.p>

          {/* Free offer pills */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.28 }}
            className="flex flex-wrap gap-2 mb-7">
            {[
              { icon: FiGift,       text: 'Free 100 Credits',        color: 'bg-green-50 border-green-200 text-green-700' },
              { icon: FiCreditCard, text: 'No Credit Card Required', color: 'bg-purple-50 border-purple-200 text-purple-700' },
            ].map(({ icon, text, color }) => (
              <span key={text} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${color}`}>
                <SafeIcon icon={icon} className="text-sm" /> {text}
              </span>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.33 }}
            className="flex flex-wrap gap-3">
            <button onClick={() => navigate('/signup')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-blue-200 transition-all hover:scale-[1.02] hover:shadow-blue-300 text-sm">
              Start Recording Now <SafeIcon icon={FiArrowRight} />
            </button>
            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-200 text-gray-700 font-bold px-7 py-3.5 rounded-full shadow-sm transition-all hover:shadow-md text-sm">
              <SafeIcon icon={FiPlay} className="text-blue-600" /> How It Works
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="flex items-center gap-6 mt-10">
            {[['40%', 'Fewer Disputes'], ['2×', 'COD Acceptance'], ['1000+', 'Brands']].map(([val, label]) => (
              <div key={label} className="text-center">
                <div className="text-2xl font-extrabold text-blue-600">{val}</div>
                <div className="text-xs text-gray-500 font-medium">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="relative flex justify-center">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};

// ── Hero Visual ────────────────────────────────────────────────────────────
const HeroVisual = () => (
  <div className="relative w-full max-w-sm">
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
      <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 h-52 flex items-center justify-center">
        <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" alt="Packing" className="w-full h-full object-cover opacity-70" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> REC
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center">
            <SafeIcon icon={FiCamera} className="text-white text-2xl" />
          </div>
        </div>
        <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-white/70 rounded-tr" />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-white/70 rounded-bl" />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-white/70 rounded-br" />
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-bold text-gray-900 text-sm">Order #ORD-2847</div>
            <div className="text-xs text-gray-400">Packaging in progress…</div>
          </div>
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiCheckCircle} className="text-green-600 text-sm" />
          </div>
        </div>
        <div className="flex gap-2">
          <button className="flex-1 bg-blue-600 text-white text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1">
            <SafeIcon icon={FiMail} className="text-xs" /> Email Customer
          </button>
          <button className="flex-1 bg-gray-100 text-gray-700 text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1">
            <SafeIcon icon={FiDownload} className="text-xs" /> Save to PC
          </button>
        </div>
      </div>
    </div>

    {/* Floating email notification */}
    <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
      className="absolute -bottom-5 -right-5 bg-white rounded-xl shadow-xl border border-gray-100 px-4 py-3 flex items-center gap-3 w-52">
      <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
        <SafeIcon icon={FiMail} className="text-blue-600 text-sm" />
      </div>
      <div>
        <div className="text-xs font-bold text-gray-900">Customer notified</div>
        <div className="text-[10px] text-gray-400">Video link sent via Email</div>
      </div>
    </motion.div>

    {/* Floating trust badge */}
    <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
      className="absolute -top-5 -left-5 bg-white rounded-xl shadow-xl border border-gray-100 px-4 py-3 flex items-center gap-2">
      <SafeIcon icon={FiShield} className="text-green-500 text-lg" />
      <span className="text-xs font-bold text-gray-800">Dispute Resolved</span>
    </motion.div>
  </div>
);

// ── Quick Info Strip ───────────────────────────────────────────────────────
const quickFacts = [
  { icon: FiMonitor,  text: 'Works on PC with Webcam' },
  { icon: FiMail,     text: 'Email Notifications' },
  { icon: FiDownload, text: 'Save Videos Locally' },
  { icon: FiClock,    text: '6 Months Cloud Storage' },
  { icon: FiGift,     text: '100 Free Credits' },
];

const QuickInfoStrip = () => (
  <div className="bg-blue-600 py-3 overflow-hidden">
    <div className="flex items-center gap-10 animate-none">
      <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-6 sm:gap-10">
        {quickFacts.map(({ icon, text }) => (
          <div key={text} className="flex items-center gap-2 text-white text-sm font-medium whitespace-nowrap">
            <SafeIcon icon={icon} className="text-blue-200 text-base" />
            {text}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Benefits ───────────────────────────────────────────────────────────────
const benefits = [
  { icon: FiAlertOctagon, title: 'Reduce Customer Disputes', color: 'text-red-500',    bg: 'bg-red-50' },
  { icon: FiShield,       title: 'Prevent False Claims',     color: 'text-blue-500',   bg: 'bg-blue-50' },
  { icon: FiTrendingUp,   title: 'Increase COD Acceptance',  color: 'text-green-500',  bg: 'bg-green-50' },
  { icon: FiUsers,        title: 'Build Customer Trust',     color: 'text-purple-500', bg: 'bg-purple-50' },
];

const Benefits = () => (
  <section className="py-20 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Built for D2C Brands</h2>
        <p className="text-gray-500 max-w-xl mx-auto">Everything you need to protect your business and delight your customers.</p>
      </FadeUp>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {benefits.map(({ icon, title, color, bg }, i) => (
          <FadeUp key={title} delay={i * 0.08}>
            <div className="group bg-white border border-gray-100 rounded-2xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default">
              <div className={`w-14 h-14 ${bg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                <SafeIcon icon={icon} className={`text-2xl ${color}`} />
              </div>
              <div className="font-bold text-gray-900 text-sm leading-snug">{title}</div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ── Features ───────────────────────────────────────────────────────────────
const features = [
  { icon: FiCamera,     title: 'Webcam Recording',          desc: 'Record packaging directly from your PC using any standard webcam — no phone needed.' },
  { icon: FiMail,       title: 'Email Customer Notify',     desc: 'Customers are automatically notified via email with their shipment video link.' },
  { icon: FiDownload,   title: 'Save Videos to Your PC',    desc: 'Download and keep a local copy of every packaging video on your computer.' },
  { icon: FiCloud,      title: '6-Month Cloud Storage',     desc: 'Videos are securely stored in the cloud and accessible for up to 6 months.' },
  { icon: FiWeight,     title: 'Weight Dispute Resolution', desc: 'Record the weight measurement on camera to resolve courier weight disputes instantly.' },
  { icon: FiShield,     title: 'Dispute Protection',        desc: 'Video evidence protects you from false damage, missing item, or wrong product claims.' },
  { icon: FiShare2,     title: 'Easy Integrations',         desc: 'Works seamlessly with Shopify, WooCommerce, and any website builder or eCommerce platform.' },
  { icon: FiZap,        title: 'Simple Setup',              desc: 'Just a PC and webcam — sign up and start recording in minutes, no tech skills needed.' },
  { icon: FiTrendingUp, title: 'Increase COD Acceptance',   desc: 'Build customer confidence so more buyers choose Cash on Delivery without hesitation.' },
];

const Features = () => (
  <section id="features" className="py-20 bg-gradient-to-b from-slate-50 to-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center mb-14">
        <span className="inline-block bg-blue-50 text-blue-600 text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">Features</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Everything You Need to Build Trust</h2>
        <p className="text-gray-500 max-w-xl mx-auto">A complete toolkit to protect your brand and keep customers happy.</p>
      </FadeUp>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map(({ icon, title, desc }, i) => (
          <FadeUp key={title} delay={i * 0.06}>
            <div className="group bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl hover:border-blue-100 hover:-translate-y-1 transition-all duration-300 h-full">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-blue-700 transition-all">
                <SafeIcon icon={icon} className="text-white text-xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-base mb-2">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ── Integrations ───────────────────────────────────────────────────────────
const integrations = [
  { name: 'Shopify',          color: 'bg-green-50 border-green-200',  text: 'text-green-700',  logo: '🛍️' },
  { name: 'WooCommerce',      color: 'bg-purple-50 border-purple-200', text: 'text-purple-700', logo: '🛒' },
  { name: 'Any Website Builder', color: 'bg-blue-50 border-blue-200', text: 'text-blue-700',   logo: '⚡' },
];

const Integrations = () => (
  <section id="integrations" className="py-20 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center mb-12">
        <span className="inline-block bg-indigo-50 text-indigo-600 text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">Integrations</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Works with Your Platform</h2>
        <p className="text-gray-500 max-w-xl mx-auto">
          ShipAssure integrates with Shopify, WooCommerce, and any website builder. If you sell online, ShipAssure works for you.
        </p>
      </FadeUp>
      <div className="flex flex-wrap justify-center gap-6">
        {integrations.map(({ name, color, text, logo }, i) => (
          <FadeUp key={name} delay={i * 0.1}>
            <div className={`border rounded-2xl px-10 py-8 text-center hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default ${color} min-w-[160px]`}>
              <div className="text-4xl mb-3">{logo}</div>
              <div className={`font-bold text-base ${text}`}>{name}</div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ── How It Works ───────────────────────────────────────────────────────────
const steps = [
  { icon: FiCamera,  title: 'Record Packaging',     desc: 'Open ShipAssure on your PC, point your webcam at the package, and hit record.' },
  { icon: FiCloud,   title: 'Upload Automatically', desc: 'Video is instantly stored in the cloud for 6 months. Download a local copy anytime.' },
  { icon: FiMail,    title: 'Notify via Email',     desc: 'Customer receives an email with the video link before delivery — building instant trust.' },
];

const HowItWorks = () => (
  <section id="how-it-works" className="py-20 bg-slate-50">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center mb-14">
        <span className="inline-block bg-green-50 text-green-600 text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">How It Works</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">How ShipAssure Works</h2>
        <p className="text-gray-500 max-w-xl mx-auto">Three simple steps to protect every shipment — from your PC to your customer's inbox.</p>
      </FadeUp>
      <div className="grid md:grid-cols-3 gap-8 relative">
        <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200" />
        {steps.map(({ icon, title, desc }, i) => (
          <FadeUp key={title} delay={i * 0.12}>
            <div className="relative text-center group">
              <div className="relative inline-block mb-6">
                <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-blue-200 group-hover:shadow-blue-300 group-hover:scale-105 transition-all duration-300">
                  <SafeIcon icon={icon} className="text-white text-3xl" />
                </div>
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-white border-2 border-blue-600 rounded-full text-blue-700 text-xs font-extrabold flex items-center justify-center">{i + 1}</span>
              </div>
              <h3 className="font-extrabold text-gray-900 text-lg mb-2">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">{desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ── Use Cases ──────────────────────────────────────────────────────────────
const useCases = [
  { icon: FiPackage,    title: 'Courier Dispute Proof',        desc: 'Video evidence proves the package was packed correctly before dispatch.' },
  { icon: FiWeight,     title: 'Weight Dispute Resolution',    desc: 'Show the weight reading on camera — no more courier overcharge disputes.' },
  { icon: FiShield,     title: 'Reduce Returns & Fraud',       desc: 'Stop false "empty box" or "wrong product" claims dead in their tracks.' },
  { icon: FiTrendingUp, title: 'Increase Customer Confidence', desc: 'Customers feel safe ordering when they know every shipment is recorded.' },
];

const UseCases = () => (
  <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.04%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">Why D2C Brands Love ShipAssure</h2>
        <p className="text-blue-200 max-w-xl mx-auto">Real problems, solved with video proof.</p>
      </FadeUp>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
        {useCases.map(({ icon, title, desc }, i) => (
          <FadeUp key={title} delay={i * 0.1}>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-7 hover:bg-white/20 transition-all duration-300 group h-full">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <SafeIcon icon={icon} className="text-white text-xl" />
              </div>
              <h3 className="font-bold text-white text-base mb-2">{title}</h3>
              <p className="text-blue-100 text-sm leading-relaxed">{desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ── Free Offer Banner ──────────────────────────────────────────────────────
const FreeOfferBanner = () => {
  const navigate = useNavigate();
  return (
    <section className="py-14 bg-gradient-to-r from-green-50 to-emerald-50 border-y border-green-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <FadeUp>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center shrink-0">
                <SafeIcon icon={FiGift} className="text-green-600 text-3xl" />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-1">Start with 100 Free Credits</h3>
                <p className="text-gray-500 text-sm max-w-md">
                  Sign up today and get <strong className="text-green-700">100 free recording credits</strong> — no credit card required. Each credit = one shipment video recorded and shared.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              <button onClick={() => navigate('/signup')}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-extrabold px-8 py-3.5 rounded-full shadow-lg shadow-green-200 transition-all hover:scale-[1.02] text-sm whitespace-nowrap">
                Claim Free Credits <SafeIcon icon={FiArrowRight} />
              </button>
              <span className="text-xs text-gray-400 font-medium">No credit card · No commitment</span>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

// ── Testimonials ───────────────────────────────────────────────────────────
const testimonials = [
  { name: 'Rohit S.', role: 'Founder, StyleBox', text: 'Reduced our returns by 40% in the first month. Customers love getting the video proof before delivery!', rating: 5 },
  { name: 'Priya M.', role: 'Shopify Store Owner', text: 'The email notification with the video link is brilliant. COD acceptance went up significantly since we started.', rating: 5 },
  { name: 'Arjun K.', role: 'D2C Brand Manager', text: 'Even resolved a courier weight dispute with the video. No more overcharge claims eating into our margins.', rating: 5 },
];

const Testimonials = () => (
  <section id="testimonials" className="py-20 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center mb-14">
        <div className="inline-flex items-center gap-2 bg-yellow-50 border border-yellow-100 text-yellow-700 text-sm font-bold px-5 py-2 rounded-full mb-5">
          <SafeIcon icon={FiUsers} /> Trusted by 1000+ D2C Brands
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">What Our Customers Say</h2>
      </FadeUp>
      <div className="grid md:grid-cols-3 gap-6">
        {testimonials.map(({ name, role, text, rating }, i) => (
          <FadeUp key={name} delay={i * 0.1}>
            <div className="bg-white rounded-2xl border border-gray-100 p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: rating }).map((_, j) => (
                  <SafeIcon key={j} icon={FiStar} className="text-yellow-400 text-sm" />
                ))}
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-5 italic flex-1">"{text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {name[0]}
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm">{name}</div>
                  <div className="text-gray-400 text-xs">{role}</div>
                </div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ── Final CTA ──────────────────────────────────────────────────────────────
const FinalCTA = () => {
  const navigate = useNavigate();
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <FadeUp>
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl px-8 py-16 relative overflow-hidden shadow-2xl shadow-blue-200">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-5 border border-white/20">
                <SafeIcon icon={FiGift} /> 100 Free Credits · No Credit Card Required
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">
                Start Building Trust with<br />Every Shipment
              </h2>
              <p className="text-blue-200 mb-8 text-base">
                Join D2C brands using video proof to grow faster and eliminate disputes.<br />
                Just a PC + webcam. Ready in minutes.
              </p>
              <button onClick={() => navigate('/signup')}
                className="inline-flex items-center gap-2 bg-white text-blue-700 font-extrabold px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all text-base">
                Get Started Free <SafeIcon icon={FiArrowRight} />
              </button>
              <div className="flex justify-center gap-6 mt-6 flex-wrap">
                {['No credit card', 'Free 100 credits', 'PC + Webcam only', '6-month cloud storage'].map(t => (
                  <span key={t} className="flex items-center gap-1.5 text-blue-200 text-xs font-medium">
                    <SafeIcon icon={FiCheckCircle} className="text-green-400 text-sm" /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

// ── Footer ─────────────────────────────────────────────────────────────────
const Footer = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <footer className="bg-gray-900 text-gray-400 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <ShipAssureLogo size={32} />
            <div>
              <div className="font-extrabold text-lg">
                <span className="text-white">Ship</span>
                <span className="text-blue-400">Assure</span>
              </div>
              <div className="text-xs text-gray-500">Turn Every Shipment into Verified Proof</div>
            </div>
          </div>
          <nav className="flex items-center gap-6 text-sm flex-wrap justify-center">
            {[['features', 'Features'], ['integrations', 'Integrations'], ['how-it-works', 'How It Works'], ['testimonials', 'Testimonials']].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-white transition-colors">{label}</button>
            ))}
          </nav>
          <p className="text-xs text-gray-600">© {new Date().getFullYear()} ShipAssure. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// ── Main Export ────────────────────────────────────────────────────────────
const LandingPage = () => (
  <div className="font-sans antialiased">
    <Navbar />
    <Hero />
    <QuickInfoStrip />
    <Benefits />
    <Features />
    <Integrations />
    <HowItWorks />
    <UseCases />
    <FreeOfferBanner />
    <Testimonials />
    <FinalCTA />
    <Footer />
  </div>
);

export default LandingPage;